import { defaultPortalPage } from '@hue/contracts';
import { PortalPageEditor } from '../../app/portal-page-editor.js';
import { Button, Input, MultiSelect, Switch, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { getApiError, servicesApi } from '../../lib/api.js';
import { portalApi, type PortalPublication } from '../../lib/portal-api.js';
export function CompanyPortalSettings({
  units,
  companyKey,
}: {
  units: Array<{ id: string; displayName: string }>;
  companyKey: string;
}) {
  const query = useQuery({
    queryKey: ['company', 'scheduling', 'portal-publication'],
    queryFn: portalApi.publication,
  });
  const services = useQuery({
    queryKey: ['company', 'scheduling', 'portal-service-options'],
    queryFn: async () => {
      const services: Array<{ id: string; displayName: string }> = [];
      let total = 0;
      do {
        const page = await servicesApi.list({
          activity: 'ACTIVE',
          currency: 'ALL',
          sort: 'displayName',
          direction: 'asc',
          limit: 100,
          offset: services.length,
        });
        total = page.total;
        services.push(...page.services);
        if (!page.services.length && services.length < total)
          throw new Error('Catálogo alterado durante a consulta. Tente novamente.');
      } while (services.length < total);
      return { services };
    },
    retry: false,
  });
  if (services.isPending) return <LoadingCard label="Carregando serviços para publicação" />;
  if (services.isError)
    return <ErrorCard message="Não foi possível carregar os serviços para publicação." />;
  if (query.isPending) return <LoadingCard label="Carregando publicação do portal" />;
  if (query.isError) return <ErrorCard message="Não foi possível carregar a publicação." />;
  return (
    <PortalPublicationForm
      key={query.dataUpdatedAt}
      initial={query.data}
      companyKey={companyKey}
      units={units}
      services={services.data?.services ?? []}
    />
  );
}
function PortalPublicationForm({
  initial,
  companyKey,
  units,
  services,
}: {
  initial: PortalPublication;
  companyKey: string;
  units: Array<{ id: string; displayName: string }>;
  services: Array<{ id: string; displayName: string }>;
}) {
  const [draft, setDraft] = useState<PortalPublication>({
    ...initial,
    page: initial.page ?? structuredClone(defaultPortalPage),
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (uploading) return;
    setSaving(true);
    setError(undefined);
    try {
      await portalApi.save(draft);
      await queryClient.invalidateQueries({
        queryKey: ['company', 'scheduling', 'portal-publication'],
      });
      await queryClient.invalidateQueries({ queryKey: ['public', 'portal'] });
      snackbar.success('Publicação do portal atualizada.');
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  const publicationControls = (
    <div className="space-y-5">
      <p className="text-sm text-content-muted">
        Publique somente as unidades e os serviços que deseja apresentar ao público. Nenhum cadastro
        de cliente é exposto.
      </p>
      <Switch checked={draft.published} onChange={(published) => setDraft({ ...draft, published })}>
        Publicar empresa no portal
      </Switch>
      <p className="text-xs text-content-muted">
        Endereço público da empresa: <strong>/c/{companyKey}</strong>
      </p>
      <Input
        label="Apresentação pública da empresa"
        value={draft.description}
        maxLength={1000}
        onChange={(event) => setDraft({ ...draft, description: event.target.value })}
      />
      <MultiSelect
        label="Unidades publicadas"
        options={units.map((item) => ({ value: item.id, label: item.displayName }))}
        selected={draft.unitIds}
        itemNoun="unidades"
        onChange={(unitIds) =>
          setDraft({
            ...draft,
            unitIds,
            page: {
              ...draft.page!,
              highlights: draft.page!.highlights.filter((h) => unitIds.includes(h.unitId)),
            },
          })
        }
      />
      <MultiSelect
        label="Serviços publicados"
        options={services.map((item) => ({ value: item.id, label: item.displayName }))}
        selected={draft.serviceIds}
        itemNoun="serviços"
        onChange={(serviceIds) =>
          setDraft({
            ...draft,
            serviceIds,
            page: {
              ...draft.page!,
              highlights: draft.page!.highlights.map((h) => ({
                ...h,
                serviceIds: h.serviceIds.filter((id) => serviceIds.includes(id)),
              })),
            },
          })
        }
      />
      <Switch
        checked={draft.requireRoom}
        onChange={(requireRoom) => setDraft({ ...draft, requireRoom })}
      >
        Oferecer horários somente quando houver sala disponível
      </Switch>
      <p className="text-sm text-content-muted">
        Duração e preço vêm do cadastro do serviço. O cliente só escolhe profissional quando a
        unidade permite. Solicitar ou pagar não confirma o horário.
      </p>
    </div>
  );
  return (
    <form noValidate onSubmit={(event) => void save(event)} className="space-y-6 pb-72 sm:pb-48">
      <fieldset disabled={saving || uploading} className="min-w-0">
        <PortalPageEditor
          publicationControls={publicationControls}
          onBusyChange={setUploading}
          draft={draft}
          onChange={setDraft}
          units={units}
          services={services}
          companyKey={companyKey}
        />
      </fieldset>
      <div
        role="region"
        aria-label="Ações da publicação"
        className="fixed bottom-3 right-20 z-40 max-h-[50dvh] w-[calc(100%-6.25rem)] max-w-xl overflow-y-auto rounded-xl border border-surface-border bg-surface-card/95 p-3 shadow-2xl ring-1 ring-surface-border/60 backdrop-blur-md sm:p-4"
      >
        {error ? <RequestAlert message={error} /> : null}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-content-muted">Alterações só ficam públicas após publicar.</p>
          <div className="flex flex-wrap gap-3">
            {initial.published ? (
              <Button asChild variant="outline">
                <a href={`/c/${companyKey}`} target="_blank" rel="noreferrer">
                  Ver página publicada
                </a>
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              disabled={saving || uploading}
              onClick={() =>
                setDraft({ ...initial, page: initial.page ?? structuredClone(defaultPortalPage) })
              }
            >
              Descartar alterações
            </Button>
            <Button type="submit" loading={saving} disabled={uploading}>
              Publicar alterações
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
